import type { PlaceResult } from "@/lib/api/misc";
import { searchPlacesResult } from "@/lib/api/misc";
import { normalizePlaceDisplay } from "@/components/PlaceAutocomplete.resolve";

/**
 * Submit-time typed-city resolver — the birthPlace conflation fix.
 *
 * Before this, the submit handler called `searchPlaces` (which swallows network
 * errors into `[]`) and returned `null` on anything that wasn't a confident
 * match. A misspelled city, a genuinely ambiguous one, and a flaky-network
 * lookup ALL collapsed to the same `null` → the same "pick from the dropdown"
 * error. That single conflation is the documented root of the 15,124 birthPlace
 * failures: we could never tell ambiguity from a true network fault.
 *
 * This resolver returns a DISCRIMINATED outcome so the caller maps each to the
 * right UX (rule 39: one obvious correction path):
 *   - resolved      → commit the chip and submit (a confident typed match)
 *   - ambiguous     → show a "did you mean" chip of the top suggestion
 *   - empty         → field genuinely blank / no results → "pick your city"
 *   - network_error → render the retry card; this is NOT a bad-city error
 *
 * The confidence rule: the top result must EXACTLY match what the user typed —
 * compared on the NORMALIZED form so case, trailing ", India", and stray
 * whitespace never break the match (a seeker who types "Mumbai, India" or
 * "mumbai " means Mumbai) — OR the user typed ≥4 chars and the top result's name
 * prefix-matches, OR a SINGLE result for a ≥3-char query (unambiguous by
 * construction — one gazetteer hit has nothing to be confused with).
 *
 * The forgiving-but-safe boundary matters for YMYL accuracy (rule 29): a wrong
 * birth city yields a wrong chart, so we only auto-commit when there is exactly
 * ONE plausible place; a genuinely ambiguous multi-result query still surfaces a
 * "did you mean" chip rather than guessing.
 */
export type TypedPlaceResolution =
  | { kind: "resolved"; place: PlaceResult }
  | { kind: "ambiguous"; suggestion: PlaceResult }
  | { kind: "empty" }
  | { kind: "network_error"; cause: string };

export async function resolveTypedPlace(
  query: string,
  search: typeof searchPlacesResult = searchPlacesResult
): Promise<TypedPlaceResolution> {
  const q = query.trim();
  if (!q) return { kind: "empty" };

  const res = await search(q, 5);
  if (res.status === "network_error") {
    return { kind: "network_error", cause: res.cause };
  }

  const results = res.results;
  if (!results.length) return { kind: "empty" };

  const top = results[0];
  if (isConfidentPlaceMatch(q, top, results.length)) return { kind: "resolved", place: top };

  // Multiple plausible matches and none confidently the user's → surface the
  // top as a tappable "did you mean" chip instead of hard-rejecting.
  return { kind: "ambiguous", suggestion: top };
}

function isConfidentPlaceMatch(query: string, top: PlaceResult, resultCount: number): boolean {
  const normalizedQuery = normalizePlaceDisplay(query);
  const normalizedName = normalizePlaceDisplay(top.name);
  const exact =
    normalizePlaceDisplay(top.display) === normalizedQuery || normalizedName === normalizedQuery;
  const prefix = query.length >= 4 && normalizedName.startsWith(normalizedQuery);
  const unambiguous = query.length >= 3 && resultCount === 1;
  return exact || prefix || unambiguous;
}
